import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import apiFetch from "../../../../shared/services/apiClient";

/**
 * CuentaSelect
 * ─────────────────────────────────────────────────────────────────────────────
 * Selector de cuentas contables de detalle.
 * Reemplaza el selector de "Rubro" en Facturas y el de categoría en CxP.
 *
 * Props:
 *   value       → id de la cuenta seleccionada (número)
 *   onChange    → fn(id, cuenta) → llamado cuando el usuario selecciona
 *   tipo        → "INGRESO" | "GASTO" | null (sin filtro)
 *   placeholder → texto de placeholder
 *   disabled    → boolean
 *   error       → string con mensaje de error
 *   className   → clase CSS adicional
 *
 * Comportamiento:
 *   - Muestra solo cuentas is_detalle=True y activa=True
 *   - Muestra el camino completo: código + nombre
 *   - Permite búsqueda por código o nombre
 *   - Se integra en el estilo del sistema existente
 */
export function CuentaSelect({
  value,
  onChange,
  tipo = null,
  placeholder = "Seleccionar cuenta contable…",
  disabled = false,
  error,
  className = "form-select",
}) {
  const { t } = useTranslation();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = tipo
      ? `/api/finance/catalogo/detalle/?tipo=${tipo}`
      : "/api/finance/catalogo/detalle/";

    apiFetch(url)
      .then((r) => r.json())
      .then((d) => setCuentas(Array.isArray(d) ? d : []))
      .catch(() => setCuentas([]))
      .finally(() => setLoading(false));
  }, [tipo]);

  function handleChange(e) {
    const id     = Number(e.target.value);
    const cuenta = cuentas.find((c) => c.id === id) ?? null;
    onChange(id, cuenta);
  }

  const borderStyle = error
    ? { borderColor: "#ef4444", boxShadow: "0 0 0 2px rgba(239,68,68,0.15)" }
    : undefined;

  return (
    <div>
      <select
        className={className}
        value={value || ""}
        onChange={handleChange}
        disabled={disabled || loading}
        style={borderStyle}
      >
        <option value="">
          {loading ? t("common.cargando") : placeholder}
        </option>

        {cuentas.map((c) => (
          <option key={c.id} value={c.id}>
            {c.codigo}  —  {c.nombre}
          </option>
        ))}
      </select>

      {error && (
        <div style={{
          color: "#f87171", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.7rem" }} />
          {error}
        </div>
      )}

      {!loading && cuentas.length === 0 && (
        <div style={{
          color: "#f59e0b", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-triangle-exclamation" style={{ fontSize: "0.7rem" }} />
          {tipo
            ? t("catalogo.sinCuentasTipo", { tipo })
            : t("catalogo.sinCuentas")
          }
        </div>
      )}
    </div>
  );
}

/**
 * CuentaSelectSearch
 * ─────────────────────────────────────────────────────────────────────────────
 * Variante con búsqueda en tiempo real — para formularios donde hay muchas cuentas.
 * Muestra un input de búsqueda y un dropdown filtrado.
 */
export function CuentaSelectSearch({
  value,
  onChange,
  tipo = null,
  placeholder = "Buscar cuenta contable…",
  disabled = false,
  error,
}) {
  const { t } = useTranslation();
  const [cuentas,  setCuentas]  = useState([]);
  const [search,   setSearch]   = useState("");
  const [open,     setOpen]     = useState(false);
  const [loading,  setLoading]  = useState(true);
  const ref = useRef(null);

  // Cargar cuentas
  useEffect(() => {
    const url = tipo
      ? `/api/finance/catalogo/detalle/?tipo=${tipo}`
      : "/api/finance/catalogo/detalle/";
    apiFetch(url)
      .then((r) => r.json())
      .then((d) => setCuentas(Array.isArray(d) ? d : []))
      .catch(() => setCuentas([]))
      .finally(() => setLoading(false));
  }, [tipo]);

  // Cerrar al click fuera
  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const seleccionada = cuentas.find((c) => c.id === value);
  const filtradas    = cuentas.filter(
    (c) =>
      !search ||
      c.codigo.toLowerCase().includes(search.toLowerCase()) ||
      c.nombre.toLowerCase().includes(search.toLowerCase())
  );

  function seleccionar(cuenta) {
    onChange(cuenta.id, cuenta);
    setSearch("");
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* Input de búsqueda / valor seleccionado */}
      <div
        style={{
          display: "flex", alignItems: "center",
          background: "var(--bg-input)", border: `1px solid ${error ? "#ef4444" : "var(--border-card)"}`,
          borderRadius: 6, padding: "8px 10px", cursor: disabled ? "not-allowed" : "pointer",
          gap: 8,
        }}
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
      >
        <i className="fa-solid fa-book-open" style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
        <span style={{
          flex: 1, fontSize: "0.85rem",
          color: seleccionada ? "var(--text-primary)" : "var(--text-muted)",
        }}>
          {seleccionada
            ? `${seleccionada.codigo} — ${seleccionada.nombre}`
            : loading ? t("common.cargando") : placeholder
          }
        </span>
        <i className={`fa-solid fa-chevron-${open ? "up" : "down"}`}
          style={{ color: "var(--text-muted)", fontSize: "0.65rem" }} />
      </div>

      {/* Dropdown */}
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
          background: "var(--bg-card)", border: "1px solid var(--border-card)",
          borderRadius: 8, zIndex: 9999, overflow: "hidden",
          boxShadow: "0 8px 24px rgba(0,0,0,0.4)", maxHeight: 280,
        }}>
          {/* Búsqueda */}
          <div style={{
            padding: "8px 10px", borderBottom: "1px solid var(--border-card)",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            <i className="fa-solid fa-magnifying-glass"
              style={{ color: "var(--text-muted)", fontSize: "0.75rem" }} />
            <input
              autoFocus
              type="text"
              placeholder={t("catalogo.buscarPlaceholder")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{
                flex: 1, background: "none", border: "none",
                color: "var(--text-primary)", fontSize: "0.83rem", outline: "none",
              }}
            />
          </div>

          {/* Lista */}
          <div style={{ overflowY: "auto", maxHeight: 220 }}>
            {filtradas.length === 0 ? (
              <div style={{ padding: "12px 14px", color: "var(--text-muted)", fontSize: "0.82rem" }}>
                {search ? t("catalogo.sinResultados") : t("catalogo.sinCuentas")}
              </div>
            ) : (
              filtradas.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => seleccionar(c)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    width: "100%", padding: "9px 14px",
                    background: c.id === value ? "rgba(249,115,22,0.1)" : "transparent",
                    border: "none", textAlign: "left", cursor: "pointer",
                    borderBottom: "1px solid var(--border-card)",
                    transition: "background 0.1s",
                  }}
                  onMouseOver={(e) => {
                    if (c.id !== value) e.currentTarget.style.background = "var(--hover-bg)";
                  }}
                  onMouseOut={(e) => {
                    if (c.id !== value) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{
                    fontFamily: "monospace", fontSize: "0.75rem",
                    color: "#f97316", minWidth: 110,
                  }}>
                    {c.codigo}
                  </span>
                  <span style={{ color: "var(--text-primary)", fontSize: "0.83rem", flex: 1 }}>
                    {c.nombre}
                  </span>
                  {c.id === value && (
                    <i className="fa-solid fa-check" style={{ color: "#f97316", fontSize: "0.7rem" }} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}

      {error && (
        <div style={{
          color: "#f87171", fontSize: "0.75rem",
          marginTop: 4, display: "flex", alignItems: "center", gap: 4,
        }}>
          <i className="fa-solid fa-circle-exclamation" style={{ fontSize: "0.7rem" }} />
          {error}
        </div>
      )}
    </div>
  );
}
